/**
 * Company Products API Client
 * Products belonging to manufacturing companies
 */

import apiClient from '../client';

const BASE_URL = '/wholesale';

// ===== HELPER: Parse batch input =====

/**
 * Parse batch tile codes from various formats
 * Supports: comma-separated, hyphen-separated, newline-separated
 */
export const parseBatchInput = (input) => {
  let codes = [];
  
  // Split by newlines first
  const lines = input.split(/[\n\r]+/);
  
  for (const line of lines) {
    // Remove leading hyphens/dashes and trim
    const cleanLine = line.replace(/^\s*[-–—]\s*/, '').trim();
    
    if (cleanLine.includes(',')) {
      // Comma separated
      codes.push(...cleanLine.split(',').map(s => s.trim()).filter(Boolean));
    } else if (cleanLine.includes('-') && !/^[\w\d]+$/.test(cleanLine)) {
      // Hyphen separated (but not a single code with hyphen in it)
      codes.push(...cleanLine.split(/\s*[-–—]\s*/).map(s => s.trim()).filter(Boolean));
    } else if (cleanLine) {
      codes.push(cleanLine);
    }
  }
  
  // Remove duplicates
  return [...new Set(codes)];
};

// ===== GET PRODUCTS =====

/**
 * Get all products with optional filters
 */
export const getProducts = async (filters = {}) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/products`, { params: filters });
    return response.data?.data || response.data || [];
  } catch (error) {
    console.error('Error fetching products:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch products',
      status: error.response?.status,
    };
  }
};

/**
 * Get products by company with optional grouping
 */
export const getCompanyProducts = async (companyId, options = {}) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/companies/${companyId}/products`, { 
      params: options 
    });
    return response.data?.data || response.data || [];
  } catch (error) {
    console.error('Error fetching company products:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch company products',
      status: error.response?.status,
    };
  }
};

/**
 * Get products grouped by category for a company
 */
export const getCompanyProductsGrouped = async (companyId) => {
  return getCompanyProducts(companyId, { grouped: true });
};

// ===== GET PRODUCT BY ID =====

/**
 * Get product by ID
 */
export const getProductById = async (id) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/products/${id}`);
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch product',
      status: error.response?.status,
    };
  }
};

// ===== CREATE PRODUCT =====

/**
 * Create a single product for a company
 */
export const createProduct = async (companyId, data) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/companies/${companyId}/products`, data);
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Error creating product:', error);
    throw {
      message: error.response?.data?.error || 'Failed to create product',
      status: error.response?.status,
    };
  }
};

/**
 * Batch create products for a company
 * @param {number} companyId - Company ID
 * @param {object} data - { size_category_id, tile_type_id, tile_codes[] } OR { size_category_id, tile_type_id, raw_input }
 */
export const batchCreateProducts = async (companyId, data) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/companies/${companyId}/products/batch`, data);
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Error batch creating products:', error);
    throw {
      message: error.response?.data?.error || 'Failed to batch create products',
      status: error.response?.status,
    };
  }
};

// ===== UPDATE PRODUCT =====

/**
 * Update a product
 */
export const updateProduct = async (id, data) => {
  try {
    const response = await apiClient.patch(`${BASE_URL}/products/${id}`, data);
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Error updating product:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update product',
      status: error.response?.status,
    };
  }
};

// ===== IMAGE MANAGEMENT =====

/**
 * Update all product images
 */
export const updateProductImages = async (id, images) => {
  try {
    const response = await apiClient.put(`${BASE_URL}/products/${id}/images`, { images });
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Error updating product images:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update product images',
      status: error.response?.status,
    };
  }
};

/**
 * Add a single image to product
 * @param {number} id - Product ID
 * @param {File|string} image - File object or image URL
 */
export const addProductImage = async (id, image) => {
  try {
    let response;
    
    if (image instanceof File) {
      // Upload file using FormData
      const formData = new FormData();
      formData.append('image', image);
      
      response = await apiClient.post(`${BASE_URL}/products/${id}/images/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } else {
      // Use URL directly
      response = await apiClient.post(`${BASE_URL}/products/${id}/images`, { image_url: image });
    }
    
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Error adding product image:', error);
    throw {
      message: error.response?.data?.error || 'Failed to add product image',
      status: error.response?.status,
    };
  }
};

/**
 * Remove an image from product by index
 */
export const removeProductImage = async (id, imageIndex) => {
  try {
    const response = await apiClient.delete(`${BASE_URL}/products/${id}/images/${imageIndex}`);
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Error removing product image:', error);
    throw {
      message: error.response?.data?.error || 'Failed to remove product image',
      status: error.response?.status,
    };
  }
};

// ===== DELETE PRODUCT =====

/**
 * Batch delete products by IDs
 * @param {number[]} productIds - Array of product IDs to delete
 */
export const batchDeleteProducts = async (productIds) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/products/batch-delete`, { product_ids: productIds });
    return response.data;
  } catch (error) {
    console.error('Error batch deleting products:', error);
    throw {
      message: error.response?.data?.error || 'Failed to delete products',
      status: error.response?.status,
    };
  }
};

/**
 * Delete a product
 */
export const deleteProduct = async (id) => {
  try {
    const response = await apiClient.delete(`${BASE_URL}/products/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting product:', error);
    throw {
      message: error.response?.data?.error || 'Failed to delete product',
      status: error.response?.status,
    };
  }
};

export default {
  parseBatchInput,
  getProducts,
  getCompanyProducts,
  getCompanyProductsGrouped,
  getProductById,
  createProduct,
  batchCreateProducts,
  updateProduct,
  updateProductImages,
  addProductImage,
  removeProductImage,
  batchDeleteProducts,
  deleteProduct,
};
